import React, { useEffect, useRef, useState } from 'react';
import { autoUpdate, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react';
import { useTheme } from 'styled-components';

import { Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { IconButton } from 'components/Button';
import { RENDERED_BAND_SELECTOR } from 'containers/Tiptap/lib/header-footer-dom';
import { HeaderFooterZone, ZONE_LABELS } from 'containers/Tiptap/lib/header-footer-zone';

import { HeaderFooterHoverStyles, StyledEditButtonFloat } from './styles';

const EDIT_BUTTON_ATTR = 'data-page-zone-edit';

const zoneOf = (el: HTMLElement): HeaderFooterZone => (el.classList.contains('tiptap-page-header') ? 'header' : 'footer');

const getZonePageNumber = (el: HTMLElement, zone: HeaderFooterZone): number | null => {
  const raw = zone === 'header' ? el.dataset.headerPageNumber : el.dataset.footerPageNumber;
  const pageNumber = Number(raw);
  return Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : null;
};

const hoverableBand = (target: EventTarget | null): HTMLElement | null => {
  const band = (target as HTMLElement | null)?.closest<HTMLElement>(RENDERED_BAND_SELECTOR) ?? null;
  return band?.dataset.editing === 'true' ? null : band;
};

const useHoveredBand = (root: HTMLElement | null, openKey: string | null): HTMLElement | null => {
  const [hovered, setHovered] = useState<HTMLElement | null>(null);
  const currentRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Pages rebuilds the bands when an overlay closes; drop a detached band so the button doesn't
    // float at 0,0 (same guard as Tiptap's DOCX template).
    const observer = new MutationObserver(() => {
      if (currentRef.current && !currentRef.current.isConnected) set(null);
    });

    function set(next: HTMLElement | null): void {
      if (currentRef.current === next) return;
      currentRef.current?.removeAttribute('data-hovered');
      currentRef.current = next;
      next?.setAttribute('data-hovered', 'true');
      if (next && root) observer.observe(root, { childList: true, subtree: true });
      else observer.disconnect();
      setHovered(next);
    }

    if (!root) {
      set(null);
      return undefined;
    }

    const onMouseOver = (event: MouseEvent): void => {
      set(hoverableBand(event.target));
    };
    const onMouseOut = (event: MouseEvent): void => {
      const band = (event.target as HTMLElement | null)?.closest<HTMLElement>(RENDERED_BAND_SELECTOR);
      const related = event.relatedTarget as HTMLElement | null;
      if (!band || related?.closest(`[${EDIT_BUTTON_ATTR}]`) || (related && band.contains(related))) return;
      set(null);
    };

    root.addEventListener('mouseover', onMouseOver);
    root.addEventListener('mouseout', onMouseOut);
    return () => {
      root.removeEventListener('mouseover', onMouseOver);
      root.removeEventListener('mouseout', onMouseOut);
      set(null);
    };
  }, [root, openKey]);

  return hovered;
};

interface ZoneEditButtonProps {
  band: HTMLElement;
  onEdit: (zone: HeaderFooterZone, pageNumber: number) => void;
}

const ZoneEditButton: React.FC<ZoneEditButtonProps> = ({ band, onEdit }) => {
  const zone = zoneOf(band);
  const inset = parseFloat(useTheme().spacing(1));
  const { refs, floatingStyles } = useFloating({
    elements: { reference: band },
    placement: 'top-end',
    middleware: [
      offset(({ rects }) => ({ mainAxis: -(rects.floating.height + inset), crossAxis: -inset })),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });
  const label = `Edit ${ZONE_LABELS[zone].label.toLowerCase()}`;

  const handleClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    const pageNumber = getZonePageNumber(band, zone);
    if (pageNumber) onEdit(zone, pageNumber);
  };

  return (
    <FloatingPortal>
      <StyledEditButtonFloat ref={refs.setFloating} style={floatingStyles} {...{ [EDIT_BUTTON_ATTR]: zone }}>
        <Tooltip title={`${label}, or double-click the ${zone}`} placement="top">
          <IconButton
            size="small"
            aria-label={label}
            data-testid={`header-footer-edit-${zone}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClick}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </StyledEditButtonFloat>
    </FloatingPortal>
  );
};

interface HeaderFooterHoverOverlayProps {
  root: HTMLElement | null;
  openKey: string | null;
  onEdit: (zone: HeaderFooterZone, pageNumber: number) => void;
}

export const HeaderFooterHoverOverlay: React.FC<HeaderFooterHoverOverlayProps> = ({ root, openKey, onEdit }) => {
  const hovered = useHoveredBand(root, openKey);

  return (
    <>
      <HeaderFooterHoverStyles />
      {hovered && <ZoneEditButton band={hovered} onEdit={onEdit} />}
    </>
  );
};
